import {
  Alert,
  Button,
  Card,
  CardHeader,
  FormField,
  Input,
  NumberStepper,
  Spinner,
  Stack,
  Text,
  Textarea,
  useToast,
} from '@ui';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { formErrors, errorMessage } from '../../../lib/errors';
import { useSession } from '../../identity/session';
import { useSalesSettings, useSaveSalesSettings, type SalesSettings } from '../api';
import styles from './SalesPages.module.css';

/** Invoice number format, payment term and footer (INVC-002, INVC-008). */
export function SalesSettingsPage() {
  const { t } = useTranslation();
  const settings = useSalesSettings();
  if (!settings.data) {
    return settings.error ? (
      <Alert tone="danger">{errorMessage(settings.error)}</Alert>
    ) : (
      <Spinner label={t('common.loading')} />
    );
  }
  return <SettingsForm key={settings.data.version} settings={settings.data} />;
}

function SettingsForm({ settings }: { settings: SalesSettings }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { can } = useSession();
  const save = useSaveSalesSettings();
  const readOnly = !can('invoices.write');
  const [format, setFormat] = useState(settings.invoice_number_format);
  const [term, setTerm] = useState<number | null>(settings.payment_term_days);
  const [footer, setFooter] = useState(settings.invoice_footer ?? '');
  const errors = formErrors(save.error, {
    format: ['invoice_number_format.'],
    term: ['payment_term.'],
    footer: ['invoice_footer'],
  });
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate(
      {
        version: settings.version,
        body: {
          invoice_number_format: format,
          payment_term_days: term ?? 0,
          invoice_footer: footer.trim() || null,
        },
      },
      { onSuccess: () => toast.show(t('common.saved')) },
    );
  };

  return (
    <Stack gap={6}>
      {readOnly && <Alert tone="info">{t('sales.settings.readOnly')}</Alert>}
      <Card aria-labelledby="invoice-settings-title">
        <CardHeader id="invoice-settings-title" title={t('sales.settings.title')} />
        <Stack as="form" gap={4} onSubmit={onSubmit} noValidate>
          {errors.general && <Alert tone="danger">{errors.general}</Alert>}
          <div className={styles.settingsField}>
            <FormField
              label={t('sales.settings.format')}
              hint={t('sales.settings.formatHint', { next: settings.next_number })}
              error={errors.field.format}
            >
              {(p) => (
                <Input
                  {...p}
                  value={format}
                  readOnly={readOnly}
                  onChange={(e) => setFormat(e.target.value)}
                />
              )}
            </FormField>
          </div>
          <div className={styles.settingsField}>
            <FormField
              label={t('sales.settings.term')}
              hint={t('sales.settings.termHint')}
              error={errors.field.term}
            >
              {(p) => (
                <NumberStepper
                  {...p}
                  value={term}
                  min={0}
                  max={365}
                  unit={t('sales.settings.days')}
                  disabled={readOnly}
                  onChange={setTerm}
                  labels={{ decrement: t('common.decrease'), increment: t('common.increase') }}
                />
              )}
            </FormField>
          </div>
          <FormField label={t('sales.settings.footer')} error={errors.field.footer}>
            {(p) => (
              <Textarea
                {...p}
                rows={3}
                value={footer}
                readOnly={readOnly}
                onChange={(e) => setFooter(e.target.value)}
              />
            )}
          </FormField>
          <Text size="sm" tone="muted">
            {t('sales.settings.smallBusiness')}
          </Text>
          {!readOnly && (
            <div>
              <Button type="submit" loading={save.isPending}>
                {t('common.save')}
              </Button>
            </div>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
